import { useState } from "react";
import { FollowUpInput } from "@/components/FollowUpInput";
import { askFollowUp, solutionToContext, type SolutionData } from "@/lib/solve";

interface FollowUpThreadProps {
  solution: SolutionData;
  originalQuestion?: string;
  outputLanguage?: string;
}

interface ThreadEntry {
  q: string;
  a: string | null;
  error?: string;
}

/**
 * Chat thread under a solution: sends free-form follow-up questions to the AI
 * and renders each question/answer pair as it resolves.
 */
export function FollowUpThread({ solution, originalQuestion, outputLanguage = "en" }: FollowUpThreadProps) {
  const [followUps, setFollowUps] = useState<ThreadEntry[]>([]);
  const [busy, setBusy] = useState(false);

  const handleFollowUp = async (message: string) => {
    if (busy) return;
    let index = 0;
    setFollowUps((prev) => {
      index = prev.length;
      return [...prev, { q: message, a: null }];
    });
    setBusy(true);
    try {
      const { answer } = await askFollowUp({
        followUpQuestion: message,
        originalQuestion,
        solutionContext: solutionToContext(solution),
        outputLanguage,
      });
      setFollowUps((prev) => prev.map((f, i) => (i === index ? { ...f, a: answer } : f)));
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not answer that right now.";
      setFollowUps((prev) => prev.map((f, i) => (i === index ? { ...f, a: null, error: msg } : f)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ml-9 md:ml-[50px]">
      {followUps.length > 0 && (
        <div className="space-y-3 pb-3" data-testid="follow-up-thread">
          {followUps.map((f, i) => (
            <div key={i} className="space-y-2">
              <p className="text-sm font-semibold text-ink bg-brand/5 border border-rule rounded-xl px-3 py-2">
                {f.q}
              </p>
              {f.a ? (
                <p className="whitespace-pre-wrap text-sm text-ink-muted bg-card border border-rule rounded-xl px-3 py-2">
                  {f.a}
                </p>
              ) : f.error ? (
                <p className="text-sm text-destructive px-3">{f.error}</p>
              ) : (
                <p className="text-sm text-ink-muted px-3 animate-pulse">Thinking…</p>
              )}
            </div>
          ))}
        </div>
      )}
      <FollowUpInput onSend={handleFollowUp} disabled={busy} />
    </div>
  );
}